"use client";

import { Spinner } from "@/components/ui/spinner";

import { useEffect, useSyncExternalStore } from "react";
import { useAuthSession } from "@/lib/auth/auth-provider";
import {
  AUTH_LOGOUT_INTENT_STORAGE_KEY,
  beginAuthRedirect,
  clearAuthRedirectLock,
  clearLogoutIntent,
  hasLogoutIntent,
} from "@/lib/auth-client";
import { Button } from "@/components/ui/button";

function getAuthErrorMessage(authError: string): string {
  switch (authError) {
    case "MissingCSRF":
      return "La sesion de autenticacion se quedo inconsistente. Reintenta el inicio de sesion.";
    case "Configuration":
      return "Hubo un problema al completar el login. Reintenta el inicio de sesion.";
    case "AccessDenied":
      return "El proveedor rechazo el acceso. Revisa tus credenciales o permisos.";
    default:
      return "No se pudo completar la autenticacion. Reintenta el inicio de sesion.";
  }
}

function subscribeLogoutIntent(callback: () => void): () => void {
  const browserWindow = globalThis.window;
  if (!browserWindow) {
    return () => {};
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key === AUTH_LOGOUT_INTENT_STORAGE_KEY) {
      callback();
    }
  };

  browserWindow.addEventListener("storage", handleStorage);
  return () => {
    browserWindow.removeEventListener("storage", handleStorage);
  };
}

function handleAuthErrorEffect(authError: string | undefined, waitingForLogoutCompletion: boolean): void {
  if (!authError || waitingForLogoutCompletion) return;

  console.warn(`[AUTH] Error en callback/signin: ${authError}. Esperando reintento manual.`);
  clearAuthRedirectLock();
}

function handleLogoutCompletionEffect(
  shouldForceFreshLogin: boolean,
  waitingForLogoutCompletion: boolean,
): void {
  if (!waitingForLogoutCompletion && !shouldForceFreshLogin) return;

  clearAuthRedirectLock();
}

function handleLoginEffect(
  authError: string | undefined,
  callbackUrl: string,
  login: (callbackUrl?: string, forceFreshLogin?: boolean) => Promise<void>,
  shouldForceFreshLogin: boolean,
  waitingForLogoutCompletion: boolean,
): void {
  if (waitingForLogoutCompletion || authError || !beginAuthRedirect()) return;

  if (shouldForceFreshLogin) {
    clearLogoutIntent();
    void login(callbackUrl, true);
    return;
  }

  void login(callbackUrl);
}

type AuthLandingPageProps = {
  authError?: string;
  callbackUrl: string;
  loggedOut: boolean;
};

export function AuthLandingPage({
  authError,
  callbackUrl,
  loggedOut,
}: AuthLandingPageProps) {
  const { login } = useAuthSession();
  const logoutIntent = useSyncExternalStore(subscribeLogoutIntent, hasLogoutIntent, () => false);
  const waitingForLogoutCompletion = logoutIntent && !loggedOut;
  const shouldForceFreshLogin = loggedOut;
  const authErrorMessage = authError ? getAuthErrorMessage(authError) : null;

  useEffect(() => {
    handleAuthErrorEffect(authError, waitingForLogoutCompletion);
  }, [authError, waitingForLogoutCompletion]);

  useEffect(() => {
    handleLogoutCompletionEffect(shouldForceFreshLogin, waitingForLogoutCompletion);
  }, [shouldForceFreshLogin, waitingForLogoutCompletion]);

  useEffect(() => {
    handleLoginEffect(
      authError,
      callbackUrl,
      login,
      shouldForceFreshLogin,
      waitingForLogoutCompletion,
    );
  }, [authError, callbackUrl, login, shouldForceFreshLogin, waitingForLogoutCompletion]);

  if (authErrorMessage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900">No se pudo iniciar la sesion</h1>
          <p className="mt-2 text-gray-600">{authErrorMessage}</p>
          <Button
            className="mt-6"
            onClick={() => {
              clearAuthRedirectLock();
              clearLogoutIntent();
              void login(callbackUrl, shouldForceFreshLogin);
            }}
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="text-center">
        <Spinner size={64} className="text-neutral-900 mx-auto" />
        <p className="mt-2 text-gray-600">
          {shouldForceFreshLogin ? "Redirigiendo a Keycloak..." : "Redirigiendo..."}
        </p>
      </div>
    </div>
  );
}
